import React, { useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon, FolderIcon, CloudIcon } from '@heroicons/react/24/outline';

interface Engagement {
  id: string;
  name: string;
  number: string;
}

interface EngagementSubscription {
  id: string;
  name: string | null;
  subscriptionKey: string;
  azureSubscriptionId?: string | null;
  awsAccountId?: string | null;
  gcpProjectId?: string | null;
  providerType?: string;
}

interface Resource {
  id: string;
  name: string;
  resourceGroupName: string;
  resourceType: string;
  resourceId: string;
  cloudProvider: string;
  region: string | null;
  status: string;
  syncedAt: string | null;
  lastSyncStatus: string | null;
  Engagement: Engagement;
  EngagementSubscription: EngagementSubscription | null;
  createdAt: string;
  updatedAt: string;
}

interface ResourceGroup {
  name: string;
  count: number;
  engagement: {
    id: string;
    name: string;
    number: string;
  };
  resources: Resource[];
}

interface ResourceTreeViewProps {
  resourceGroups: ResourceGroup[];
  onResourceSelect?: (resource: Resource, resourceGroup: ResourceGroup) => void;
  onResourceGroupSelect?: (resourceGroup: ResourceGroup) => void;
  selectedResourceIds?: string[];
  selectable?: boolean;
}

const ResourceTreeView: React.FC<ResourceTreeViewProps> = ({
  resourceGroups,
  onResourceSelect,
  onResourceGroupSelect,
  selectedResourceIds = [],
  selectable = false
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800';
      case 'deleted':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSyncStatusBadge = (status: string | null, syncedAt: string | null) => {
    if (!status || !syncedAt) {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Manual</span>;
    }
    
    if (status === 'success') {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Synced</span>;
    } else {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Failed</span>;
    }
  };

  const handleResourceClick = (resource: Resource, resourceGroup: ResourceGroup) => {
    if (onResourceSelect) {
      onResourceSelect(resource, resourceGroup);
    }
  };

  const handleResourceGroupClick = (resourceGroup: ResourceGroup) => {
    if (onResourceGroupSelect) {
      onResourceGroupSelect(resourceGroup);
    }
  };

  if (resourceGroups.length === 0) {
    return (
      <div className="text-center py-12">
        <CloudIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No resources found</h3>
        <p className="mt-1 text-sm text-gray-500">
          No resources match your current filters.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="space-y-1">
        {resourceGroups.map((group) => {
          const groupKey = `${group.name}-${group.engagement.id}`;
          const isExpanded = expandedGroups.has(groupKey);

          return (
            <div key={groupKey} className="w-full">
              {/* Resource Group Node */}
              <div
                className="flex items-center py-2 px-3 text-sm cursor-pointer hover:bg-gray-100 rounded-md transition-colors duration-150"
                onClick={() => {
                  toggleGroup(groupKey);
                  handleResourceGroupClick(group);
                }}
              >
                <div className="flex items-center flex-1">
                  <button
                    type="button"
                    className="mr-1 flex-shrink-0 w-4 h-4 flex items-center justify-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroup(groupKey);
                    }}
                  >
                    {group.resources.length > 0 ? (
                      isExpanded ? (
                        <ChevronDownIcon className="h-3 w-3 text-gray-600" />
                      ) : (
                        <ChevronRightIcon className="h-3 w-3 text-gray-600" />
                      )
                    ) : (
                      <div className="w-3 h-3" />
                    )}
                  </button>
                  <FolderIcon className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {group.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {group.engagement.name} ({group.engagement.number})
                    </div>
                  </div>
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0">
                    {group.count}
                  </span>
                </div>
              </div>

              {/* Resources List - Tree Child Nodes */}
              {isExpanded && group.resources.length > 0 && (
                <div className="ml-5 space-y-1">
                  {group.resources.map((resource) => (
                    <div
                      key={resource.id}
                      className={`flex items-center py-2 px-3 text-sm rounded-md transition-colors duration-150 ${
                        selectable ? 'cursor-pointer hover:bg-gray-100' : ''
                      } ${
                        selectedResourceIds.includes(resource.id) 
                          ? 'bg-blue-50 ring-1 ring-blue-200' 
                          : ''
                      }`}
                      onClick={() => handleResourceClick(resource, group)}
                    >
                      <div className="flex items-center flex-1">
                        <div className="w-4 h-4 mr-1 flex-shrink-0">
                          <div className="w-2 h-2 border-l border-b border-gray-300 ml-1"></div>
                        </div>
                        {selectable && (
                          <input
                            type="checkbox"
                            checked={selectedResourceIds.includes(resource.id)}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                            className="mr-2 h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
                          />
                        )}
                        <CloudIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {resource.name}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {resource.resourceType}
                            {resource.region && ` • ${resource.region}`}
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                          <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded-full ${getStatusBadgeClass(resource.status)}`}>
                            {resource.status}
                          </span>
                          {getSyncStatusBadge(resource.lastSyncStatus, resource.syncedAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ResourceTreeView;